import { useEffect } from "react";
import { useLocation } from "react-router";

/**
 * The public site and the app at their own addresses (ninekeep.com, app.ninekeep.com), named by
 * the server in <meta name="ninekeep-hosts">. Absent (one address, as in development), every
 * page lives everywhere and nothing moves. Keep SITE_PATH in step with apps/server/src/hosts.ts.
 */
export const SITE_PATH =
  /^\/(?:$|welcome|partners|how-it-works|pricing|waitlist|get-started|supported-vendors|resources|product|solutions|industries|customers)(?:\/|$)/;

export const hosts = (() => {
  const [site, app] = (
    document.querySelector<HTMLMetaElement>('meta[name="ninekeep-hosts"]')?.content ?? ""
  ).split(" ");
  return site && app ? { site, app } : null;
})();

export const onAppHost = () => !!hosts && location.origin === hosts.app;
export const onSiteHost = () => !!hosts && location.origin === hosts.site;

/** The public site's front page, from anywhere. */
export const siteHome = () => (hosts ? `${hosts.site}/` : "/welcome");
/** An app page, from anywhere. */
export const appHref = (path: string) => (hosts ? `${hosts.app}${path}` : path);

/**
 * Moving between pages inside the browser doesn't reach the server, so this sends a page that
 * belongs at the other address there, the way the server would.
 */
export function HostGuard() {
  const loc = useLocation();
  useEffect(() => {
    if (!hosts) return;
    const site = SITE_PATH.test(loc.pathname);
    const rest = `${loc.pathname}${loc.search}${loc.hash}`;
    if (onSiteHost() && !site) location.replace(`${hosts.app}${rest}`);
    else if (onAppHost() && site && loc.pathname !== "/") location.replace(`${hosts.site}${rest}`);
  }, [loc.pathname, loc.search, loc.hash]);
  return null;
}
